import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_BASE_URL, authFetch } from '../../config/api';
import PageHeader from '../../components/PageHeader';

/*
 * Editor review of non-EU MRL mappings (docs/designs/multi-jurisdiction-mrl.md).
 * A register's commodity or substance feeds product-page verdicts only once it
 * is mapped to an EU product or residue AND reviewed. Every save here locks the
 * row (manual_override), so the nightly pipeline never changes it again; Reset
 * hands it back to the automatic mapping.
 */

type Kind = 'commodity' | 'substance';
type Status = 'unreviewed' | 'reviewed' | 'rejected' | 'not_applicable' | 'all';
type Action = 'accept' | 'edit' | 'reject' | 'reset';

interface Counts {
  limits: number;
  substances: number;
  substances_mapped: number;
  commodities_total: number;
  commodities_mapped: number;
  pending_review: number;
}

interface AdminJurisdiction {
  code: string;
  name: string;
  authority: string | null;
  licence: string | null;
  non_commercial_only: boolean;
  enabled: boolean;
  disabled_by_env: boolean;
  last_loaded_at: string | null;
  counts: Counts | null;
}

interface MappingRow {
  jurisdiction: string;
  source_key: string;
  name: string;
  target_id: string | null;
  target_name: string | null;
  target_code: string | null;
  target_parent: string | null;
  scope: string;
  group_code: string | null;
  qualifier: string | null;
  cas_number: string | null;
  match_method: string | null;
  match_confidence: number | null;
  match_note: string | null;
  reviewed: boolean;
  manual_override: boolean;
  limits_count: number;
}

interface TargetOption {
  id: string;
  code: string | null;
  name: string | null;
  parent_name: string | null;
  child_count: number;
  descendant_count: number;
}

const PAGE_SIZE = 50;
const BASE = `${API_BASE_URL}/api/admin/mrl`;

const STATUS_LABELS: Record<Status, string> = {
  unreviewed: 'Needs review',
  reviewed: 'Reviewed',
  rejected: 'Rejected (no EU equivalent)',
  not_applicable: 'Not applicable (processed or feed)',
  all: 'All',
};

async function getJson<T>(url: string): Promise<T> {
  const res = await authFetch(url);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

async function fetchJurisdictions(): Promise<AdminJurisdiction[]> {
  const data = await getJson<{ jurisdictions: AdminJurisdiction[] }>(`${BASE}/jurisdictions`);
  return data.jurisdictions;
}

async function fetchMappings(kind: Kind, jurisdiction: string, status: Status, q: string, offset: number) {
  const params = new URLSearchParams({ kind, jurisdiction, status, limit: String(PAGE_SIZE), offset: String(offset) });
  if (q.length >= 2) params.set('q', q);
  return getJson<{ total: number; rows: MappingRow[] }>(`${BASE}/mappings?${params}`);
}

async function fetchOptions(kind: Kind): Promise<TargetOption[]> {
  const data = await getJson<{ options: TargetOption[] }>(`${BASE}/options?kind=${kind}`);
  return data.options;
}

async function saveMapping(p: {
  kind: Kind;
  jurisdiction: string;
  key: string;
  action: Action;
  target_id?: string;
  allow_group?: boolean;
}) {
  const res = await authFetch(
    `${BASE}/mappings/${p.kind}/${encodeURIComponent(p.jurisdiction)}/${encodeURIComponent(p.key)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: p.action, target_id: p.target_id, allow_group: p.allow_group }),
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.error ?? `Save failed (HTTP ${res.status})`);
  }
}

async function setEnabled(p: { code: string; enabled: boolean }) {
  const res = await authFetch(`${BASE}/jurisdictions/${encodeURIComponent(p.code)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled: p.enabled }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.error ?? `Update failed (HTTP ${res.status})`);
  }
}

// Same rules as the admin PUT in routes/mrl.ts, which enforces them: a single
// commodity mapped onto an EU node with children shows on every product under
// it, so the editor confirms; milk-fat tolerances only warn.
function groupMessage(jurisdiction: string, o: TargetOption): string {
  return o.parent_name == null
    ? `This maps a single ${jurisdiction} commodity onto a top-level EU category of ${o.descendant_count} products; ` +
        `product pages never inherit from it, so its ${jurisdiction} limits would show on the category alone.`
    : `This maps a single ${jurisdiction} commodity onto an EU group; ` +
        `its ${jurisdiction} limits would show on all ${o.descendant_count} products under it.`;
}

const MILK_FAT = /^milk,\s*fat\b/i;
const MILK_FAT_WARNING =
  "Milk-fat tolerances are on a fat basis, not whole milk. Mapped here they show as this product's limits " +
  'and take precedence over a whole-milk "Milk" mapping on a group above it.';

function methodText(r: MappingRow): string {
  if (r.manual_override) return r.match_method === 'rejected' ? 'Editor: no EU equivalent' : 'Editor';
  switch (r.match_method) {
    case 'cas': return 'CAS number';
    case 'name': return 'Exact name';
    case 'llm': return `Assisted · ${Math.round((r.match_confidence ?? 0) * 100)}%`;
    case 'processed': return 'Processed product';
    case 'feed': return 'Animal feed';
    case 'conditional': return 'Conditional tolerance: editor only';
    default: return 'Not mapped yet';
  }
}

function statusBadge(r: MappingRow) {
  if (r.manual_override) return <span className="staging-badge staging-badge--approved">Locked</span>;
  if (r.reviewed) return <span className="staging-badge staging-badge--approved">Reviewed</span>;
  return <span className="staging-badge staging-badge--pending">Needs review</span>;
}

function TargetSelect({ kind, label, value, options, onChange }: {
  kind: Kind;
  label: string;
  value: string;
  options: TargetOption[];
  onChange: (v: string) => void;
}) {
  const grouped = useMemo(() => {
    if (kind === 'substance') return null;
    const m = new Map<string, TargetOption[]>();
    for (const o of options) {
      const g = o.parent_name?.trim() || 'Top level';
      const list = m.get(g) ?? [];
      list.push(o);
      m.set(g, list);
    }
    return m;
  }, [kind, options]);
  return (
    <select className="form-select" aria-label={label} value={value} onChange={e => onChange(e.target.value)}>
      <option value="">-- Choose EU {kind === 'commodity' ? 'product' : 'residue'} --</option>
      {grouped
        ? Array.from(grouped.entries()).map(([group, items]) => (
            <optgroup key={group} label={group}>
              {items.map(o => (
                <option key={o.id} value={o.id}>
                  {o.code ? `${o.code} · ` : ''}{o.name}
                  {o.child_count > 0 ? ` [group · ${o.descendant_count} products]` : ''}
                </option>
              ))}
            </optgroup>
          ))
        : options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
    </select>
  );
}

export default function MrlMappingPage() {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<Kind>('commodity');
  const [status, setStatus] = useState<Status>('unreviewed');
  const [jurisdiction, setJurisdiction] = useState('US');
  const [search, setSearch] = useState('');
  const [searchSubmitted, setSearchSubmitted] = useState('');
  const [page, setPage] = useState(0);
  const [pending, setPending] = useState<Map<string, string>>(new Map());
  const [editingKey, setEditingKey] = useState<string | null>(null);
  // source_key -> the group target the editor confirmed; a new pick unticks it.
  const [groupConfirmed, setGroupConfirmed] = useState<Map<string, string>>(new Map());
  const [errorText, setErrorText] = useState<string | null>(null);

  const jurisdictionsQuery = useQuery(['mrl-admin-jurisdictions'], fetchJurisdictions);
  const foreign = (jurisdictionsQuery.data ?? []).filter(j => j.code !== 'EU');

  const mappingsQuery = useQuery(
    ['mrl-mappings', kind, jurisdiction, status, searchSubmitted, page],
    () => fetchMappings(kind, jurisdiction, status, searchSubmitted, page * PAGE_SIZE),
    { keepPreviousData: true },
  );
  const optionsQuery = useQuery(['mrl-options', kind], () => fetchOptions(kind), { staleTime: 600_000 });
  const optionById = useMemo(
    () => new Map<string, TargetOption>((optionsQuery.data ?? []).map(o => [o.id, o])),
    [optionsQuery.data],
  );

  const invalidate = () => {
    queryClient.invalidateQueries(['mrl-mappings']);
    queryClient.invalidateQueries(['mrl-mapping-summary']);
    queryClient.invalidateQueries(['mrl-admin-jurisdictions']);
  };

  const saveMut = useMutation(saveMapping, {
    onSuccess: (_d, vars) => {
      setPending(prev => {
        const next = new Map(prev);
        next.delete(vars.key);
        return next;
      });
      setGroupConfirmed(prev => {
        const next = new Map(prev);
        next.delete(vars.key);
        return next;
      });
      setErrorText(null);
      invalidate();
    },
    onError: (err: Error) => setErrorText(err.message),
  });

  const enableMut = useMutation(setEnabled, {
    onSuccess: () => {
      setErrorText(null);
      invalidate();
    },
    onError: (err: Error) => setErrorText(err.message),
  });

  const rows = mappingsQuery.data?.rows ?? [];
  const total = mappingsQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Clearing the last rows of the last page leaves the offset past the end.
  useEffect(() => {
    if (mappingsQuery.data && mappingsQuery.data.rows.length === 0 && page > 0) {
      setPage(Math.max(0, Math.ceil(mappingsQuery.data.total / PAGE_SIZE) - 1));
    }
  }, [mappingsQuery.data, page]);

  function resetPaging() {
    setPage(0);
    setPending(new Map());
    setGroupConfirmed(new Map());
    setEditingKey(null);
  }

  // Before the options load this is null, and the server's 400 is the guard.
  function groupTarget(r: MappingRow, target: string | null | undefined): TargetOption | null {
    if (kind !== 'commodity' || r.scope !== 'item' || !target) return null;
    const o = optionById.get(target);
    return o && o.child_count > 0 ? o : null;
  }

  function needsConfirm(r: MappingRow, target: string | null | undefined): boolean {
    const group = groupTarget(r, target);
    return !!group && groupConfirmed.get(r.source_key) !== group.id;
  }

  function act(r: MappingRow, action: Action, target?: string) {
    if (action === 'reject' && !window.confirm(`Mark "${r.name}" as having no EU equivalent? It will never feed a verdict.`)) return;
    const sent = action === 'edit' ? target : action === 'accept' ? r.target_id : null;
    if (needsConfirm(r, sent)) {
      setErrorText(`"${r.name}" maps onto an EU group: tick the confirmation on its row first.`);
      return;
    }
    const allowGroup = groupTarget(r, sent) ? true : undefined;
    saveMut.mutate({ kind, jurisdiction: r.jurisdiction, key: r.source_key, action, target_id: target, allow_group: allowGroup });
  }

  function saveAll() {
    for (const [key, target] of pending) {
      const r = rows.find(x => x.source_key === key);
      if (r && target) act(r, 'edit', target);
    }
  }

  return (
    <div className="container admin-page">
      <PageHeader
        title="MRL Mappings"
        tagline="Match non-EU pesticide register names to EU products and residues. Only reviewed matches feed product-page verdicts."
        back={{ to: '/admin', label: 'Back to Manage' }}
        actions={total > 0 && status === 'unreviewed' ? <span className="card-badge">{total} to review</span> : undefined}
      />

      <section className="card admin-section">
        <div className="card-header">
          <h3>Jurisdictions</h3>
        </div>
        <div className="card-body admin-section__body">
          {jurisdictionsQuery.isLoading && <p className="admin-muted">Loading…</p>}
          {jurisdictionsQuery.isError && <p className="admin-error">Failed to load jurisdictions</p>}
          {jurisdictionsQuery.data && (
            <div className="data-table__wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Register</th>
                    <th>Limits</th>
                    <th>Commodities mapped</th>
                    <th>Substances mapped</th>
                    <th>Needs review</th>
                    <th>Last checked</th>
                    <th>Licence</th>
                    <th>Shown on site</th>
                  </tr>
                </thead>
                <tbody>
                  {jurisdictionsQuery.data.map(j => (
                    <tr key={j.code}>
                      <td className="admin-cell--primary">{j.code}</td>
                      <td>{j.name}</td>
                      <td className="admin-muted">{j.counts?.limits.toLocaleString() ?? '—'}</td>
                      <td className="admin-muted">
                        {j.counts ? `${j.counts.commodities_mapped} / ${j.counts.commodities_total}` : '—'}
                      </td>
                      <td className="admin-muted">
                        {j.counts ? `${j.counts.substances_mapped} / ${j.counts.substances}` : '—'}
                      </td>
                      <td className="admin-muted">{j.counts?.pending_review ?? '—'}</td>
                      <td className="admin-muted admin-cell--nowrap">
                        {j.last_loaded_at ? new Date(j.last_loaded_at).toLocaleDateString('en-GB') : '—'}
                      </td>
                      <td className="admin-muted">
                        {j.licence ?? '—'}
                        {j.non_commercial_only && (
                          <div className="admin-error">Non-commercial use only: disable before the site trades.</div>
                        )}
                      </td>
                      <td>
                        {j.code === 'EU' ? (
                          <span className="admin-muted">Always</span>
                        ) : (
                          <label className="admin-cell--nowrap">
                            <input
                              type="checkbox"
                              checked={j.enabled}
                              disabled={enableMut.isLoading}
                              onChange={e => enableMut.mutate({ code: j.code, enabled: e.target.checked })}
                            />{' '}
                            {j.enabled ? 'Enabled' : 'Disabled'}
                            {j.disabled_by_env && <div className="admin-muted">Off via MRL_DISABLED_JURISDICTIONS</div>}
                          </label>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <section className="card admin-section">
        <div className="card-header">
          <h3>Review queue</h3>
        </div>
        <div className="card-body admin-section__body">
          <div className="news-page__tabs" role="group" aria-label="Queue">
            {(['commodity', 'substance'] as Kind[]).map(k => (
              <button
                key={k}
                type="button"
                aria-pressed={kind === k}
                className={`news-page__tab ${kind === k ? 'news-page__tab--active' : ''}`}
                onClick={() => {
                  setKind(k);
                  resetPaging();
                }}
              >
                {k === 'commodity' ? 'Commodities' : 'Substances'}
              </button>
            ))}
          </div>

          <div className="admin-toolbar">
            <select
              className="form-select"
              value={jurisdiction}
              onChange={e => {
                setJurisdiction(e.target.value);
                resetPaging();
              }}
              aria-label="Jurisdiction"
            >
              {(foreign.length > 0 ? foreign : [{ code: 'US', name: 'United States (EPA)' }]).map(j => (
                <option key={j.code} value={j.code}>{j.code} · {j.name}</option>
              ))}
            </select>
            <select
              className="form-select"
              value={status}
              onChange={e => {
                setStatus(e.target.value as Status);
                resetPaging();
              }}
              aria-label="Status"
            >
              {(Object.keys(STATUS_LABELS) as Status[]).map(s => (
                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
              ))}
            </select>
            <input
              type="text"
              className="form-input admin-toolbar__search"
              aria-label="Search names"
              placeholder={`Search ${kind === 'commodity' ? 'commodity' : 'substance'} names…`}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  setSearchSubmitted(search.trim());
                  resetPaging();
                }
              }}
            />
            <button
              className="btn btn--secondary"
              onClick={() => {
                setSearchSubmitted(search.trim());
                resetPaging();
              }}
            >
              Search
            </button>
            {pending.size > 0 && (
              <button className="btn btn--primary" onClick={saveAll} disabled={saveMut.isLoading}>
                Save all ({pending.size})
              </button>
            )}
          </div>

          {errorText && <p className="admin-error">{errorText}</p>}
          {mappingsQuery.isLoading && <p className="admin-muted">Loading…</p>}
          {mappingsQuery.isError && <p className="admin-error">Failed to load mappings</p>}
          {!mappingsQuery.isLoading && rows.length === 0 && <p className="admin-empty">Nothing in this view.</p>}

          {rows.length > 0 && (
            <div className="data-table__wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{kind === 'commodity' ? 'Commodity' : 'Substance'} ({jurisdiction})</th>
                    <th>Limits</th>
                    <th>EU match</th>
                    <th>Method</th>
                    <th>Status</th>
                    <th>Change to</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => {
                    const staged = pending.get(r.source_key) ?? '';
                    // What Save or Accept would send, and whether that is a group node.
                    const outgoing = staged || (r.manual_override ? null : r.target_id);
                    const group = groupTarget(r, outgoing);
                    const currentGroup = groupTarget(r, r.target_id);
                    const milkFat = kind === 'commodity' && r.scope === 'item' && MILK_FAT.test(r.name);
                    return (
                      <tr key={r.source_key} className={staged ? 'data-table__row--changed' : ''}>
                        <td className="admin-cell--primary">
                          {r.name}
                          {r.scope === 'group' && (
                            <div className="admin-muted">Crop group {r.group_code}{r.qualifier ? ` · ${r.qualifier}` : ''}</div>
                          )}
                          {r.scope !== 'group' && r.qualifier && (
                            <div className="admin-error">Condition: {r.qualifier}. Map only if the EU product is judged under this condition.</div>
                          )}
                          {r.cas_number && <div className="admin-muted">CAS {r.cas_number}</div>}
                        </td>
                        <td className="admin-muted">{r.limits_count}</td>
                        <td>
                          {r.target_id ? (
                            <>
                              {r.target_name}
                              <div className="admin-muted">
                                {[r.target_code, r.target_parent].filter(Boolean).join(' · ')}
                              </div>
                            </>
                          ) : (
                            <span className="admin-muted">No EU match</span>
                          )}
                          {r.match_note && <div className="admin-muted">{r.match_note}</div>}
                          {currentGroup && currentGroup.id !== group?.id && (
                            <div className="admin-warning">{groupMessage(r.jurisdiction, currentGroup)}</div>
                          )}
                          {milkFat && r.target_id && !staged && <div className="admin-warning">{MILK_FAT_WARNING}</div>}
                        </td>
                        <td className="admin-muted admin-cell--nowrap">{methodText(r)}</td>
                        <td>{statusBadge(r)}</td>
                        <td>
                          {editingKey === r.source_key || staged ? (
                            // One option list at a time: 50 rows × 700 options is a slow page.
                            <TargetSelect
                              kind={kind}
                              label={`EU ${kind === 'commodity' ? 'product' : 'residue'} for ${r.name}`}
                              value={staged}
                              options={optionsQuery.data ?? []}
                              onChange={v =>
                                setPending(prev => {
                                  const next = new Map(prev);
                                  if (v) next.set(r.source_key, v);
                                  else next.delete(r.source_key);
                                  return next;
                                })
                              }
                            />
                          ) : (
                            <button
                              type="button"
                              className="btn btn--ghost btn--sm"
                              aria-label={`Change the EU match for ${r.name}`}
                              onClick={() => setEditingKey(r.source_key)}
                            >
                              Change…
                            </button>
                          )}
                          {group && (
                            <div className="admin-warning" role="alert">
                              {groupMessage(r.jurisdiction, group)}
                              <label className="admin-check">
                                <input
                                  type="checkbox"
                                  checked={groupConfirmed.get(r.source_key) === group.id}
                                  onChange={e => {
                                    const checked = e.target.checked;
                                    setGroupConfirmed(prev => {
                                      const next = new Map(prev);
                                      if (checked) next.set(r.source_key, group.id);
                                      else next.delete(r.source_key);
                                      return next;
                                    });
                                  }}
                                />
                                <span>Map to the whole group ({group.descendant_count} products)</span>
                              </label>
                            </div>
                          )}
                          {milkFat && staged && <div className="admin-warning">{MILK_FAT_WARNING}</div>}
                        </td>
                        <td className="admin-cell--nowrap">
                          {staged ? (
                            <button
                              type="button"
                              className="btn btn--primary btn--sm"
                              aria-label={`Save the EU match for ${r.name}`}
                              onClick={() => act(r, 'edit', staged)}
                              disabled={saveMut.isLoading || needsConfirm(r, staged)}
                            >
                              Save
                            </button>
                          ) : (
                            r.target_id && !r.manual_override && (
                              <button
                                type="button"
                                className="btn btn--primary btn--sm"
                                aria-label={`Accept the proposed EU match for ${r.name}`}
                                onClick={() => act(r, 'accept')}
                                disabled={saveMut.isLoading || needsConfirm(r, r.target_id)}
                              >
                                Accept
                              </button>
                            )
                          )}{' '}
                          {r.match_method !== 'rejected' && (
                            <button
                              type="button"
                              className="btn btn--danger-ghost btn--sm"
                              aria-label={`Reject ${r.name}: no EU equivalent`}
                              onClick={() => act(r, 'reject')}
                              disabled={saveMut.isLoading}
                            >
                              Reject
                            </button>
                          )}{' '}
                          {(r.manual_override || r.reviewed) && (
                            <button
                              type="button"
                              className="btn btn--ghost btn--sm"
                              aria-label={`Reset ${r.name} to automatic mapping`}
                              onClick={() => act(r, 'reset')}
                              disabled={saveMut.isLoading}
                              title="Unlock and hand back to the automatic mapping"
                            >
                              Reset
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="admin-footer">
            <div className="admin-pagination">
              <button className="btn btn--ghost btn--sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                ← Previous
              </button>
              <span className="admin-muted">Page {page + 1} of {totalPages} · {total} rows</span>
              <button
                className="btn btn--ghost btn--sm"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(p => p + 1)}
              >
                Next →
              </button>
            </div>
            {pending.size > 0 && <span className="admin-muted">{pending.size} unsaved changes</span>}
          </div>
        </div>
      </section>
    </div>
  );
}
